import { AsyncPipe } from '@angular/common';
// eslint-disable-next-line import/no-unresolved
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	Router,
	RouterModule,
	ExtraOptions,
	PreloadAllModules,
	provideRouter,
	withPreloading,
	RouteReuseStrategy,
} from '@angular/router';
import { App } from '@capacitor/app';
import { PureAbility } from '@casl/ability';
// eslint-disable-next-line import/no-unresolved
// eslint-disable-next-line import/no-unresolved
import {
	IonicRouteStrategy,
	provideIonicAngular,
	// eslint-disable-next-line import/no-unresolved
} from '@ionic/angular/standalone';
import { defineCustomElements } from '@ionic/core/loader';
import * as Sentry from '@sentry/angular';

// eslint-disable-next-line import/no-unresolved
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { provideNgxMask } from 'ngx-mask';
import { passiveSupport } from 'passive-events-support/src/utils';
import { firstValueFrom, switchMap, tap } from 'rxjs';

import { CoreModule } from 'app/core/core.module';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routing';
import { AuthService } from './app/core/auth/auth.service';
import { ThemeConfigModule } from './app/core/config/theme/theme.config.module';
import { THEME_CONFIG as ThemeConfig } from './app/core/config/theme/theme.config.types';
import { LayoutModule } from './app/layout/layout.module';
import { TimezoneInterceptor } from './app/shared/interceptors/timezone.interceptor';
import { SplashScreenModule } from './app/shared/services/splash-screen/splash-screen.module';

const routerConfig: ExtraOptions = {
	preloadingStrategy: PreloadAllModules,
	bindToComponentInputs: true,
};

passiveSupport({
	events: [
		'touchstart',
		'touchmove',
		'touchcenter',
		'touchend',
		'touchleave',
		'wheel',
		'mousewheel',
	], // or any other event types
});

async function init() {
	// Register all Ionic Web Components (+ their CSS) as defineCustomElements will
	// dynamic-import the *.entry.js bundles
	defineCustomElements(window);
	return bootstrapApplication(AppComponent, {
		providers: [
			importProvidersFrom(
				BrowserModule,
				RouterModule.forRoot(appRoutes, routerConfig),
				LoggerModule.forRoot({ level: NgxLoggerLevel.OFF }),
				CoreModule,
				LayoutModule,
				AsyncPipe,
				SplashScreenModule,
				ThemeConfigModule.forRoot(ThemeConfig),
				MatNativeDateModule,
				GoogleMapsModule,
			),
			provideNgxMask(),
			provideAppInitializer(() => {
				return inject(AuthService).signIn();
			}),
			{
				provide: ErrorHandler,
				useValue: Sentry.createErrorHandler({
					showDialog: true,
				}),
			},
			{
				provide: Sentry.TraceService,
				deps: [Router],
			},
			{
				provide: HTTP_INTERCEPTORS,
				useClass: TimezoneInterceptor,
				multi: true,
			},
			{ provide: PureAbility, useValue: new PureAbility() },
			{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
			provideIonicAngular({
				useSetInputAPI: true,
				mode: 'ios',
				innerHTMLTemplatesEnabled: true,
			}),
			provideAnimations(),
			provideRouter(appRoutes, withPreloading(PreloadAllModules)),
		],
	});
}

init().catch((err) => console.error(err));
